import { useCallback, useEffect, useRef,useState } from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Button, InputNumber, Space } from 'antd';

import SimpleColorPicker from '@src/components/ColorPicker/SimpleColorPicker';
import { rgbToHex } from '@src/packages/utils/functions';
import IntlMessages from '@src/util/IntlMessages';

import styles from './PercentageColorRules.module.less';

const MAX_RULES = 4;
const PRESET_COLORS = [
  { color: '#3F8DD8', title: 'Blue' },
  { color: '#73D13D', title: 'Green' },
  { color: '#FFC53D', title: 'Yellow' },
  { color: '#FF4D4F', title: 'Red' },
]

const PercentageColorRules = ({ value, onChange }) => {
  const [internalRules, setInternalRules] = useState([]);
  const isInternalUpdateRef = useRef(false);
  const nextIdRef = useRef(0);

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (value && typeof value === 'object') {
      setInternalRules(prevRules => {
        const rulesFromValue = Object.entries(value).map(([percentage, color]) => {
          const percentageNum = Number(percentage);
          const existingRule = prevRules.find(r => r.percentage === percentageNum);
          const id = existingRule?.id || `rule-${nextIdRef.current++}`;

          return {
            id,
            percentage: percentageNum,
            color: color.startsWith('#') ? color.substring(1) : color,
          };
        });

        const incompleteRules = prevRules.filter(
          rule =>
            (rule.percentage === null || rule.percentage === undefined) &&
            !rulesFromValue.some(r => r.id === rule.id),
        );

        return [...incompleteRules, ...rulesFromValue];
      });
    } else if (!value) {
      setInternalRules(prevRules => {
        return prevRules.filter(
          rule => rule.percentage === null || rule.percentage === undefined,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const updateValue = useCallback(
    newRules => {
      if (!onChange) return;

      const newValue = {};
      newRules.forEach(rule => {
        if (
          rule.percentage !== null &&
          rule.percentage !== undefined &&
          rule.color &&
          rule.percentage >= 0 &&
          rule.percentage <= 100
        ) {
          const cleanColor = rule.color.startsWith('#') ? rule.color.substring(1) : rule.color;
          newValue[rule.percentage.toString()] = cleanColor;
        }
      });

      isInternalUpdateRef.current = true;
      onChange(Object.keys(newValue).length > 0 ? newValue : undefined);
    },
    [onChange],
  );

  const handleAddRule = useCallback(() => {
    if (internalRules.length >= MAX_RULES) return;

    const newRule = {
      id: `rule-${nextIdRef.current++}`,
      percentage: null,
      color: '#FF0000',
    };
    const newRules = [...internalRules, newRule];
    setInternalRules(newRules);
  }, [internalRules]);

  const handleRemoveRule = useCallback(
    index => {
      const newRules = internalRules.filter((_, i) => i !== index);
      setInternalRules(newRules);
      updateValue(newRules);
    },
    [internalRules, updateValue],
  );

  const handlePercentageChange = useCallback(
    (index, newPercentage) => {
      const newRules = [...internalRules];
      const currentRule = newRules[index];
      const hadCompletePercentage =
        currentRule.percentage !== null && currentRule.percentage !== undefined;

      newRules[index] = {
        ...currentRule,
        percentage: newPercentage === null || newPercentage === undefined ? null : newPercentage,
      };

      setInternalRules(newRules);

      const isComplete =
        newPercentage !== null &&
        newPercentage !== undefined &&
        newPercentage >= 0 &&
        newPercentage <= 100 &&
        currentRule.color;

      if (isComplete || hadCompletePercentage) {
        updateValue(newRules);
      }
    },
    [internalRules, updateValue],
  );

  const handleColorChange = useCallback(
    (index, newColor) => {
      const newRules = [...internalRules];
      const colorValue = typeof newColor === 'string' ? newColor : rgbToHex(newColor);
      newRules[index] = {
        ...newRules[index],
        color: colorValue,
      };
      setInternalRules(newRules);
      updateValue(newRules);
    },
    [internalRules, updateValue],
  );

  return (
    <div className={styles.container}>
      {internalRules.map((rule, index) => (
        <div key={rule.id || `rule-${index}`} className={styles.ruleRow}>
          <Space  size="middle" className={styles.ruleContent} align="start">
            <span className={styles.label}>
              <IntlMessages id="dashboards.chart.form.counter.percentage.rules.if.less.equal" />
            </span>
            <InputNumber
              min={0}
              max={100}
              value={rule.percentage}
              onChange={val => handlePercentageChange(index, val)}
              formatter={value => (value !== null && value !== undefined ? `${value}%` : '')}
              parser={value => (value ? value.replace('%', '') : null)}
              placeholder="0"
              className={styles.percentageInput}
            />
            <span className={styles.label}>
              <IntlMessages id="dashboards.chart.form.counter.percentage.rules.will.show.color" />
            </span>
            <span className={styles.colorPickerWrapper}>
              <SimpleColorPicker
                color={
                  rule.color?.startsWith('#')
                    ? rule.color
                    : rule.color
                      ? `#${rule.color}`
                      : '#FF0000'
                }
                onChange={color => handleColorChange(index, color)}
                hexColor
                presetColors={PRESET_COLORS}
              />
            </span>
            {internalRules.length > 0 && (
              <div className={styles.deleteButtonWrapper}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveRule(index)}
                  className={styles.deleteButton}
                />
              </div>
            )}
          </Space>
        </div>
      ))}
      {internalRules.length < MAX_RULES && (
        <Button
          type="link"
          onClick={handleAddRule}
          className={styles.addButton}
          disabled={internalRules.length >= MAX_RULES}
        >
          <IntlMessages id="dashboards.chart.form.counter.percentage.rules.add.another" />
        </Button>
      )}
    </div>
  );
};

export default PercentageColorRules;
